import { Controller } from "@hotwired/stimulus"

// Celebration FX for the "I Tried This" buttons on My Tries cards: haptic
// buzz, a paper-plane confetti burst from the button, and a glow pulse on
// the button. The feedback drawer itself is rendered/animated server-side.
export default class extends Controller {
  static PLANE_COUNT = 18
  static BURST_DURATION = 1500

  // body/shadow pairs; no fk-yellow token exists, prototype values kept
  static PLANE_COLORS = [
    { body: "rgb(var(--fk-bluegreen-400))", shadow: "#4a9a95" },
    { body: "rgb(var(--fk-orange-400))", shadow: "#a0621e" },
    { body: "rgb(var(--fk-green-400))", shadow: "#4a6e4c" },
    { body: "#f0d97a", shadow: "#c9b45a" }
  ]

  celebrate(event) {
    const form = event.target.closest("form")
    if (!form || form.dataset.triedFxAction !== "complete") return

    // Turbo disables the submit button during the request; this flag also
    // debounces bursts from rapid repeat clicks after the card re-renders.
    if (this._busy) return
    this._busy = true
    setTimeout(() => { this._busy = false }, 400)

    const btn = form.querySelector("button") || this.element
    const rect = btn.getBoundingClientRect()

    navigator.vibrate?.([30, 20, 40])

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this._glow(btn)
    this._burst(rect.left + rect.width / 2, rect.top + rect.height / 2)
  }

  _glow(btn) {
    btn.classList.add("tried-fx-glow")

    // The create response turbo-streams a full card replace ~100-300ms in,
    // destroying the glowing button. Re-apply to the new card's button once
    // the stream has rendered.
    const cardId = this.element.id
    document.addEventListener("turbo:submit-end", (event) => {
      if (event.target.dataset?.triedFxAction !== "complete") return
      if (!event.detail.success) return
      requestAnimationFrame(() => requestAnimationFrame(() => {
        document
          .querySelector(`#${CSS.escape(cardId)} form[data-tried-fx-action='complete'] button`)
          ?.classList.add("tried-fx-glow")
      }))
    }, { once: true })
  }

  _burst(x, y) {
    const { PLANE_COUNT, BURST_DURATION, PLANE_COLORS } = this.constructor

    for (let i = 0; i < PLANE_COUNT; i++) {
      const palette = PLANE_COLORS[Math.floor(Math.random() * PLANE_COLORS.length)]
      const size = 20 + Math.random() * 18

      const plane = document.createElement("div")
      plane.dataset.turboTemporary = ""
      // z-index 45 keeps planes above the drawer overlay (z-40) but under
      // the drawer panel (z-50)
      plane.style.cssText = `position:fixed;left:${x}px;top:${y}px;z-index:45;pointer-events:none;will-change:transform,opacity`
      plane.innerHTML = this._planeSvg(palette, size)
      document.body.appendChild(plane)

      const angle = Math.random() * 2 * Math.PI
      const speed = 120 + Math.random() * 200
      const vx = Math.cos(angle) * speed
      const vy = Math.sin(angle) * speed - 80
      const spin = Math.random() * 540 - 270
      const flip = Math.random() > 0.5 ? " scaleX(-1)" : ""
      const initialRotate = Math.random() * 360
      const delay = Math.random() * 120

      plane.animate([
        {
          transform: `translate(-50%,-50%) rotate(${initialRotate}deg)${flip} scale(1)`,
          opacity: 1
        },
        {
          transform: `translate(calc(-50% + ${vx * 0.5}px), calc(-50% + ${vy * 0.5}px)) rotate(${initialRotate + spin * 0.5}deg)${flip} scale(0.9)`,
          opacity: 1,
          offset: 0.3
        },
        {
          transform: `translate(calc(-50% + ${vx}px), calc(-50% + ${vy}px)) rotate(${initialRotate + spin}deg)${flip} scale(0.25)`,
          opacity: 0
        }
      ], {
        duration: BURST_DURATION,
        delay,
        easing: "cubic-bezier(0.08, 0.82, 0.17, 1)",
        fill: "forwards"
      })

      setTimeout(() => plane.remove(), BURST_DURATION + delay + 100)
    }
  }

  _planeSvg({ body, shadow }, size) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 32 32">
      <path d="M2 26 L16 4 L16 20 Z" fill="${shadow}" opacity="0.55"/>
      <path d="M2 26 L30 14 L16 4 Z" fill="${body}"/>
      <path d="M16 4 L16 20 L30 14" fill="${shadow}" opacity="0.35"/>
      <line x1="16" y1="4" x2="16" y2="20" stroke="white" stroke-width="0.8" opacity="0.6"/>
    </svg>`
  }
}
